/**
 * Track mechanic: a linear track of squares 1..length with an off-board start at 0,
 * piece positions per seat, moves with an overshoot rule, jumps (chutes/ladders/shortcuts)
 * and colour-space search. Pure and deterministic; no randomness lives here.
 */

/** 'exact' = a move that would pass the end is not taken; 'stop' = the piece stops on the end. */
export type Overshoot = 'exact' | 'stop';

export interface TrackSpec {
  /** Number of squares; the last square (`length`) is the finish. Position 0 is the off-board start. */
  readonly length: number;
  /** from -> to. Landing on `from` carries the piece to `to`. */
  readonly jumps?: Readonly<Record<number, number>>;
}

export interface MoveResult {
  readonly from: number;
  /** Final resting square after any jump. */
  readonly to: number;
  /** Square reached by the move itself, before any jump. */
  readonly landed: number;
  readonly jump: 'up' | 'down' | null;
  /** The move would have passed the end. */
  readonly overshot: boolean;
  readonly finished: boolean;
}

/** Direction of the jump taken from a square, if any. */
export function jumpAt(spec: TrackSpec, square: number): { to: number; dir: 'up' | 'down' } | null {
  const to = spec.jumps?.[square];
  if (to === undefined) return null;
  return { to, dir: to > square ? 'up' : 'down' };
}

/** Move a piece `k` squares forward, applying the overshoot rule and then any jump. */
export function move(spec: TrackSpec, from: number, k: number, overshoot: Overshoot): MoveResult {
  const raw = from + k;
  const overshot = raw > spec.length;
  const landed = overshot ? (overshoot === 'stop' ? spec.length : from) : raw;
  const j = overshot && overshoot === 'exact' ? null : jumpAt(spec, landed);
  const to = j ? j.to : landed;
  return { from, to, landed, jump: j ? j.dir : null, overshot, finished: to === spec.length };
}

/** Problems with a track definition (empty = fine): jumps must stay on the board and not chain. */
export function trackProblems(spec: TrackSpec): string[] {
  const out: string[] = [];
  for (const [f, t] of Object.entries(spec.jumps ?? {})) {
    const from = Number(f);
    if (from < 1 || from >= spec.length) out.push(`jump source ${from} is off the track or on the finish`);
    if (t < 1 || t > spec.length) out.push(`jump target ${t} is off the track`);
    if (t === from) out.push(`jump ${from} goes nowhere`);
    if (spec.jumps?.[t] !== undefined) out.push(`jump ${from}->${t} chains into another jump`);
  }
  return out;
}

/**
 * Colour-space search. `spaces` is indexed by position (index 0 = start). Returns the position of the
 * `nth` (1-based) space after `from` that satisfies `match`, or null when there are not that many left.
 */
export function nthSpace<T>(spaces: readonly T[], from: number, match: (space: T) => boolean, nth = 1): number | null {
  let seen = 0;
  for (let i = Math.max(0, from + 1); i < spaces.length; i++) {
    if (match(spaces[i] as T) && ++seen === nth) return i;
  }
  return null;
}

/** All positions where `match` holds. */
export function spacesWhere<T>(spaces: readonly T[], match: (space: T) => boolean): number[] {
  return spaces.flatMap((s, i) => (match(s) ? [i] : []));
}

/** Everyone starts off-board. */
export const startPositions = (seats: number): number[] => Array.from({ length: seats }, () => 0);

/** Copy of `positions` with one seat moved. */
export const withPosition = (positions: readonly number[], seat: number, pos: number): number[] =>
  positions.map((p, i) => (i === seat ? pos : p));

/** Seats sharing a square with `seat` (excluding it). */
export const sharing = (positions: readonly number[], seat: number): number[] =>
  positions.flatMap((p, i) => (i !== seat && p === positions[seat] ? [i] : []));

/** Seat furthest along (lowest seat wins ties). */
export const leader = (positions: readonly number[]): number => positions.reduce((best, p, i) => (p > (positions[best] as number) ? i : best), 0);

/** Next seat in turn order. */
export const nextSeat = (seat: number, seats: number): number => (seat + 1) % seats;

/** Boustrophedon (serpentine) layout: square 1 bottom-left, rows alternate direction. Returns 0-based {row, col}, row 0 = top. */
export function serpentine(square: number, cols: number, rows: number): { row: number; col: number } {
  const i = square - 1;
  const fromBottom = Math.floor(i / cols);
  const c = i % cols;
  return { row: rows - 1 - fromBottom, col: fromBottom % 2 === 0 ? c : cols - 1 - c };
}

/** Ring (loop) track: position `pos` moved by `k` squares (negative = backward) on a ring of `size` squares. */
export const ringStep = (pos: number, k: number, size: number): number => (((pos + k) % size) + size) % size;

/** Which of `sides` ring quadrants each seat uses: two players sit opposite each other, otherwise seats take sides in order. */
export const spreadSides = (seats: number, sides = 4): number[] =>
  seats === 2 ? [0, sides / 2] : Array.from({ length: seats }, (_, i) => i);
